import { DAILY_BONUS, DAILY_GROUPS, DAILY_QUESTS, dailyDef, type DailyDef } from '../data/daily';
import { t } from '../../i18n';
import { addTickets } from './fortune';
import { grantExp } from './hero';
import { defaultCounters, type Hero, type SaveData } from './model';
import { addMemory } from './progression';

/** The player's calendar day (device clock), e.g. "2026-10-20". */
export function dayKey(now = new Date()): string {
  const p = (n: number): string => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${p(now.getMonth() + 1)}-${p(now.getDate())}`;
}

function previousDay(day: string): string {
  const [y, m, d] = day.split('-').map(Number);
  return dayKey(new Date(y!, m! - 1, d! - 1, 12));
}

function hash(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), 16777619);
  return h >>> 0;
}

/** One quest per group, the same all day; online and Hall quests only when they can be done. */
export function pickDaily(save: SaveData, day: string): string[] {
  const has = (src: SaveData['monsters'][number]['source']): boolean => save.monsters.some((m) => m.source === src);
  const [hasHall, hasDare, hasBoss] = [has('hall'), has('dare'), has('boss')];
  return DAILY_GROUPS.map((g, i) => {
    const pool = DAILY_QUESTS.filter((q) => q.group === g && (!q.online || save.online) && (!q.hall || hasHall) && (!q.dare || hasDare) && (!q.boss || hasBoss));
    return pool[hash(`${day}:${i}`) % pool.length]!.id;
  });
}

/** Starts a new day when the date changed: today's quests and a fresh counter baseline. */
export function rollDaily(save: SaveData, now = new Date()): boolean {
  const day = dayKey(now);
  if (save.daily.day === day) return false;
  save.daily.day = day;
  save.daily.base = { ...save.counters };
  save.daily.quests = pickDaily(save, day);
  save.daily.claimed = [];
  return true;
}

export interface DailyState {
  def: DailyDef;
  progress: number;
  claimed: boolean;
  ready: boolean;
}

/** Today's quests without changing the save (a stale day reads as a fresh one). */
export function dailyQuests(save: SaveData, now = new Date()): DailyState[] {
  const fresh = save.daily.day === dayKey(now);
  const ids = fresh ? save.daily.quests : pickDaily(save, dayKey(now));
  const base = { ...defaultCounters(), ...save.daily.base };
  return ids.flatMap((id) => {
    const def = dailyDef(id);
    if (!def) return [];
    const progress = fresh ? Math.min(def.goal, Math.max(0, save.counters[def.metric] - base[def.metric])) : 0;
    const claimed = fresh && save.daily.claimed.includes(id);
    return [{ def, progress, claimed, ready: !claimed && progress >= def.goal }];
  });
}

/** Days in a row with all three quests done, counting today or yesterday. */
export function dailyStreak(save: SaveData, now = new Date()): number {
  const today = dayKey(now);
  const last = save.daily.lastFull;
  return last === today || last === previousDay(today) ? save.daily.streak : 0;
}

export function dailyBonus(streak: number): number {
  return DAILY_BONUS.base + DAILY_BONUS.perStreakDay * (Math.min(streak, DAILY_BONUS.maxStreakDays) - 1);
}

export interface DailyClaim {
  def: DailyDef;
  /** Set when this claim finished the day: the bonus coins, the new streak and fortune tickets added (0 when full). */
  bonus: { coins: number; streak: number; tickets: number } | null;
}

export function claimDaily(save: SaveData, hero: Hero | null, id: string, now = new Date()): DailyClaim | null {
  rollDaily(save, now);
  const q = dailyQuests(save, now).find((x) => x.def.id === id);
  if (!q?.ready) return null;
  save.daily.claimed.push(id);
  save.coins += q.def.coins;
  if (hero) grantExp(hero, q.def.exp);
  if (save.daily.claimed.length < save.daily.quests.length) return { def: q.def, bonus: null };

  const today = save.daily.day;
  save.daily.streak = save.daily.lastFull === previousDay(today) ? save.daily.streak + 1 : 1;
  save.daily.lastFull = today;
  const coins = dailyBonus(save.daily.streak);
  save.coins += coins;
  const tickets = addTickets(save, 1);
  addMemory(save, {
    kind: 'quest',
    title: t('All daily quests done!'),
    text: t('+{coins} bonus coins. Streak: {n} days.', { coins, n: save.daily.streak }),
    sticker: 'star',
  });
  return { def: q.def, bonus: { coins, streak: save.daily.streak, tickets } };
}

/** Daily quests that became ready since `before` (ids ready earlier). */
export function newlyReadyDaily(save: SaveData, before: ReadonlySet<string>, now = new Date()): DailyDef[] {
  return dailyQuests(save, now)
    .filter((q) => q.ready && !before.has(q.def.id))
    .map((q) => q.def);
}
